import { useEffect, useRef, useState } from "react"

import { VStack, Flex, Text, Box } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom" // Import useNavigate

import { useAppDispatch, useAppSelector } from "app/hooks"
import GreenCheckedIcon from "components/icons/rbo/green_checked_icon"
import RightArrow from "components/icons/rightArrow"
import {
  COMPLETE,
  INCOMPLETE,
  NOT_STARTED,
  useExtractPathDetails,
} from "pages/listing-details-with-step/src/common"
import {
  isAvailabilityUpdated,
  isCancellationPolicyUpdated,
  isLocationUpdated,
  isMinimumAgeUpdated,
  isPdpPlanUpdated,
  isPricingUpdated,
  isUpdatedImportCalendarLink,
} from "pages/listing-details-with-step/src/left-section/onboardingStatusUtils"
import { apiClient } from "services/client"
import { PDPType } from "services/common"
import { PropertyData } from "services/rbo/onboarding-status-data"
import { selectCurrentPmc } from "store/auth/authSlice"
import {
  getOnBoardingStatusDataApi,
  selectCancellationPolicy,
  selectCurrentAddress,
  selectCurrentPricingValue,
  selectImportCalendarLink,
  selectMinimumAgeValue,
  selectOnBoardingStatusData,
  selectPdpPlan,
  selectPropertiesData,
  selectAvailabilityValue,
} from "store/rbo/rboSlice"

const NavigationRadioList = ({
  setSidebarOpen,
}: {
  setSidebarOpen?: (isOpen: boolean) => void
}) => {
  const dispatch = useAppDispatch() // Initialize dispatch function
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({}) // Define type for menuRefs
  const currentPmc = useAppSelector(selectCurrentPmc)
  const navigate = useNavigate() // Initialize navigate function
  const { unitID, pageName } = useExtractPathDetails() // Get propertyId from the URL
  const onBoardingStatusData = useAppSelector(selectOnBoardingStatusData)
  const propertyData = useAppSelector(selectPropertiesData)
  const currentAddress = useAppSelector(selectCurrentAddress)
  const currentPricingValue = useAppSelector(selectCurrentPricingValue)
  const currentAvailabilityValue = useAppSelector(selectAvailabilityValue)
  const currentPdpPlan = useAppSelector(selectPdpPlan)
  const currentCancellationPolicy = useAppSelector(selectCancellationPolicy)
  const currentMinimumAgeValue = useAppSelector(selectMinimumAgeValue)
  const currentImportCalendarLink = useAppSelector(selectImportCalendarLink)
  const [selectedOption, setSelectedOption] = useState<string>(pageName)

  // menuItems array to store the items to be displayed in the navigation radio list
  const menuItems = [
    {
      label: "Location",
      value: "location-details",
      path: `/listing-details/${unitID}/location-details`,
      status: onBoardingStatusData?.locationStatus || NOT_STARTED,
    },
    {
      label: "Pricing, Discounts, and Fees",
      value: "pricing",
      path: `/listing-details/${unitID}/pricing`,
      status: onBoardingStatusData?.pricingStatus || NOT_STARTED,
    },
    {
      label: "Availability",
      value: "availability",
      path: `/listing-details/${unitID}/availability`,
      status: onBoardingStatusData?.availabilityStatus || NOT_STARTED,
    },
    {
      label: "Property Damage Protection",
      value: "pdp",
      path: `/listing-details/${unitID}/pdp`,
      status: onBoardingStatusData?.propertyDamageStatus || NOT_STARTED,
    },
    {
      label: "Cancellation Policy",
      value: "cancellation-policy",
      path: `/listing-details/${unitID}/cancellation-policy`,
      status: onBoardingStatusData?.cancellationStatus || NOT_STARTED,
    },
    {
      label: "Minimum Age Requirement",
      value: "minimum-age-requirement",
      path: `/listing-details/${unitID}/minimum-age-requirement`,
      status: onBoardingStatusData?.minAgeStatus || NOT_STARTED,
    },
    {
      label: "Calendar Sync",
      value: "calendar-sync",
      path: `/listing-details/${unitID}/calendar-sync`,
      status: onBoardingStatusData?.calendarStatus || NOT_STARTED,
    },
  ]

  const hasLocation = isLocationUpdated(
    onBoardingStatusData,
    currentAddress,
    propertyData?.property,
  )
  const hasPricing = isPricingUpdated(
    onBoardingStatusData,
    currentPricingValue,
    propertyData?.property,
  )
  const hasAvailability = isAvailabilityUpdated(
    onBoardingStatusData,
    currentAvailabilityValue,
    propertyData?.property?.rboPropertyData,
  )
  const hasPdpPlan = isPdpPlanUpdated(
    onBoardingStatusData,
    currentPdpPlan,
    propertyData?.property?.pdp || "",
  )
  const hasCancellationPolicy = isCancellationPolicyUpdated(
    onBoardingStatusData,
    currentCancellationPolicy,
    propertyData?.property?.propData?.cancellation_policy?.policy[0],
  )
  const hasMinimumAge = isMinimumAgeUpdated(
    onBoardingStatusData,
    currentMinimumAgeValue,
    String(propertyData?.property?.propData?.minimum_age) || "18",
  )
  const hasCalendarSync = isUpdatedImportCalendarLink(
    onBoardingStatusData,
    currentImportCalendarLink,
    propertyData?.importCalendarLink || "",
  )

  // Set the selected option to the current page name on page load
  useEffect(() => {
    setSelectedOption(pageName)
  }, [pageName])

  // Scroll to the selected option on page load
  useEffect(() => {
    if (selectedOption === pageName) {
      menuRefs.current[selectedOption]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    }
  }, [selectedOption, pageName])

  // handle onboarding status data update on page change Api call
  const updateStatusData = async () => {
    let payloadData = {
      ...onBoardingStatusData,
      propertyId:
        onBoardingStatusData?.propertyId || propertyData?.property?.id || "", // Ensure it's always a string
      pmcId: currentPmc?.pmc_id || "",
    }
    if (hasLocation && pageName === "location-details") {
      payloadData = {
        ...payloadData,
        location: JSON.stringify(currentAddress),
        locationStatus: INCOMPLETE,
      }
    }
    if (hasPricing && pageName === "pricing") {
      payloadData = {
        ...payloadData,
        pricing: JSON.stringify(currentPricingValue),
        pricingStatus: INCOMPLETE,
      }
    }
    if (hasAvailability && pageName === "availability") {
      payloadData = {
        ...payloadData,
        availability: JSON.stringify(currentAvailabilityValue),
        availabilityStatus: INCOMPLETE,
      }
    }
    if (hasPdpPlan && pageName === "pdp") {
      payloadData = {
        ...payloadData,
        propertyDamage: JSON.stringify({
          isMyOwnPlan: currentPdpPlan === PDPType?.Existing,
          isWhimstayPlan: currentPdpPlan === PDPType?.Whimstay,
        }),
        propertyDamageStatus: INCOMPLETE,
      }
    }
    if (hasCancellationPolicy && pageName === "cancellation-policy") {
      payloadData = {
        ...payloadData,
        cancellationPolicy: JSON.stringify({
          Partial: currentCancellationPolicy?.id === "Partial",
          Standard: currentCancellationPolicy?.id === "Standard",
          Moderate: currentCancellationPolicy?.id === "Moderate",
          Strict: currentCancellationPolicy?.id === "Strict",
        }),
        cancellationStatus: INCOMPLETE,
      }
    }
    if (hasMinimumAge && pageName === "minimum-age-requirement") {
      payloadData = {
        ...payloadData,
        minAgeRequirement: currentMinimumAgeValue,
        minAgeStatus: INCOMPLETE,
      }
    }
    if (hasCalendarSync && pageName === "calendar-sync") {
      payloadData = {
        ...payloadData,
        calendar: JSON.stringify({
          importCalendar: currentImportCalendarLink,
          exportCalendar: currentImportCalendarLink,
        }),
        calendarStatus: INCOMPLETE,
      }
    }
    await apiClient
      .updateOnboardingStatusData({ data: payloadData as PropertyData })
      .then(async (res) => {
        if (res.status === 200) {
          await dispatch(
            getOnBoardingStatusDataApi({
              params: {
                propertyId: propertyData?.property?.id || "",
              },
            }),
          ).catch(() => {})
        }
        return true
      })
      .catch((error) => {
        console.error("Error updating onboarding status data:", error)
      })
  }

  const shouldUpdate = () => {
    const updateMap: Record<string, boolean> = {
      "location-details": hasLocation,
      pricing: hasPricing,
      availability: hasAvailability,
      pdp: hasPdpPlan,
      "cancellation-policy": hasCancellationPolicy,
      "minimum-age-requirement": hasMinimumAge,
      "calendar-sync": hasCalendarSync,
    }
    return updateMap[pageName] || false
  }
  const handleSelection = (item: { value: string; path: string }) => {
    setSelectedOption(item.value)
    if (shouldUpdate()) {
      // eslint-disable-next-line no-void
      void updateStatusData() // Call the updateStatusData function to update the onboarding status data
    }
    navigate(item.path) // Navigate to the corresponding path
    setSidebarOpen?.(false) // Close the sidebar
  }

  // Render the icon based on the status of the item
  const renderIcon = (status: string) => {
    switch (status) {
      case COMPLETE: // Green check icon
        return <GreenCheckedIcon />
      case INCOMPLETE: // orange check icon
        return <GreenCheckedIcon fillColor='#C67B2A' />
      case NOT_STARTED: // white circle icon
      default:
        return (
          <Box
            borderRadius='50%'
            minWidth={5}
            w={5}
            h={5}
            border='2px solid white'
          />
        )
    }
  }

  return (
    <VStack
      align='start'
      w='100%'
      spacing={0}
      pb={5}
      px={{ base: 4, md: 7, xl: 10 }}
    >
      {menuItems.map((item) => (
        <Flex
          key={item.label}
          ref={(el) => {
            if (el) menuRefs.current[item.value] = el
          }}
          bg={selectedOption === item.value ? "#00454F" : "transparent"}
          px={4}
          py={5}
          borderRadius='8px'
          w='100%'
          alignItems='center'
          justifyContent='space-between'
          cursor='pointer'
          onClick={() => handleSelection(item)}
        >
          <Flex>
            {renderIcon(item?.status || NOT_STARTED)}
            <Box ml='11px'>
              <Text
                color='white'
                fontWeight={600}
                fontSize={{ base: 16, md: 18 }}
                lineHeight='21.94px'
              >
                {item.label}
              </Text>
            </Box>
          </Flex>
          {selectedOption === item.value && <RightArrow />}
        </Flex>
      ))}
    </VStack>
  )
}

export default NavigationRadioList
